import { memo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CreativesGridSkeleton from '@/components/common/CreativesGridSkeleton'
import CreativeCard from './CreativeCard'
import {
    isCreativeEligibleForSelection,
    hasVisibleAssetVariations,
} from '@/store/adCreativesStore'
import { Button } from '@/components/ui/button'
import { AdsManagerIconWhite } from '@/components/icons/AdsManagerIconWhite'

const CreativesList = memo(
    ({
        isLoading,
        error,
        filteredCreatives,
        hasActiveFilters,
        isCreativeSelected,
        onToggleSelection,
        onDeleteCreative = null, // Make optional
    }) => {
        const navigate = useNavigate()
        const { id } = useParams()
        // Error Message
        if (error) {
            return <div className="tw:bg-[#f85149] tw:text-white tw:p-3 tw:rounded-md tw:mb-6">{error}</div>
        }

        // Loading State
        if (isLoading) {
            return <CreativesGridSkeleton count={8} />
        }

        // Empty State
        if (filteredCreatives.length === 0) {
            // Show the new empty state design when no creatives exist
            return (
                <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-20 tw:px-5">
                    {/* Icon Container */}
                    <div className="tw:w-20 tw:h-20 tw:rounded-full tw:bg-[#2B3B4C] tw:flex tw:items-center tw:justify-center tw:mb-6">
                        <AdsManagerIconWhite size={56} />
                    </div>

                    {/* Text Content */}
                    <h3 className="tw:text-white tw:!text-sm tw:!font-normal tw:!mb-1 tw:text-center">
                        No creatives added yet
                    </h3>
                    <h3 className="tw:text-white tw:!text-sm tw:!font-normal tw:!mb-1 tw:text-center">
                        Create your first ad to
                    </h3>
                    <h3 className="tw:text-white tw:!text-sm tw:!font-normal tw:!mb-6 tw:text-center">get started</h3>
                    {/* Action Button */}
                    <Button
                        onClick={() => navigate(`/campaigns/media-buying/${id}/new-creative`)}
                        className="tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:text-white tw:px-6 tw:py-2 hover:tw:bg-gray-700"
                    >
                        New Ad Creative
                    </Button>
                </div>
            )
        }

        // Creatives Grid
        return (
            <div className="tw:grid tw:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] tw:gap-5 tw:pb-20">
                {filteredCreatives.map((creative) => {
                    const isSelectable =
                        isCreativeEligibleForSelection(creative) &&
                        hasVisibleAssetVariations(creative)
                    return (
                        <CreativeCard
                            key={creative.id}
                            creative={creative}
                            isSelected={isCreativeSelected(creative.id)}
                            onToggleSelection={onToggleSelection}
                            onDelete={onDeleteCreative}
                            campaignId={id}
                            isSelectable={isSelectable}
                        />
                    )
                })}
            </div>
        )
    },
)

CreativesList.displayName = 'CreativesList'

export default CreativesList
